// components/EfficiencyOverviewPanel.js
import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@material-ui/core';
import {
  Speed,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  DirectionsCar,
  Build,
  Info,
  FlashOn,
  SwapHoriz
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  metricsGrid: {
    marginBottom: theme.spacing(2)
  },
  metricCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5)
  },
  crewEfficiencyItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: theme.shadows[2],
      transform: 'translateY(-1px)'
    }
  },
  efficiencyBar: {
    display: 'flex',
    height: 30,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  serviceBar: {
    backgroundColor: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 'bold'
  },
  driveBar: {
    backgroundColor: theme.palette.warning.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: 'bold'
  },
  rankingList: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    }
  },
  scoreChip: {
    fontWeight: 'bold',
    minWidth: 32
  },
  alertsSection: {
    marginTop: theme.spacing(2),
    maxHeight: 200,
    overflow: 'auto'
  },
  summaryBox: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  quickActionButton: {
    marginLeft: theme.spacing(1),
    fontSize: '0.75rem',
    padding: '2px 8px',
    minWidth: 'auto'
  }
}));

const EfficiencyOverviewPanel = ({ crewMetrics, rankings, insights, summary, onCrewClick }) => {
  const classes = useStyles();

  // Get score color based on grade
  const getScoreColor = (score) => {
    switch (score) {
      case 'A': return 'primary';
      case 'B': return 'primary';
      case 'C': return 'default';
      case 'D': return 'secondary';
      case 'F': return 'error';
      default: return 'default';
    }
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp style={{ color: '#4caf50' }} />;
    if (trend === 'declining') return <TrendingDown style={{ color: '#f44336' }} />;
    return null;
  };

  return (
    <Paper className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">
          Crew Efficiency Overview
        </Typography>
        <Tooltip title="Efficiency is calculated as service time / total time">
          <IconButton size="small">
            <Info />
          </IconButton>
        </Tooltip>
      </div>

      {/* Summary Box */}
      <Box className={classes.summaryBox}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {summary.averageEfficiency}%
            </Typography>
            <Typography variant="caption">
              Avg Efficiency
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {summary.crewsWorking}/{summary.totalCrews}
            </Typography>
            <Typography variant="caption">
              Active Crews
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {summary.totalOvertime}h
            </Typography>
            <Typography variant="caption">
              Total Overtime
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {summary.avgMilesPerStop}mi
            </Typography>
            <Typography variant="caption">
              Avg Miles/Stop
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Top/Bottom Performers */}
      <Grid container spacing={2} style={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
            Top Performers
          </Typography>
          <div style={{ height: 'calc(100% - 30px)', overflow: 'auto' }}>
            {rankings.efficiency.slice(0, 5).map((crew, index) => {
              const metrics = crewMetrics[crew.crewId];
              if (!metrics) return null;
              
              return (
                <div 
                  key={crew.crewId} 
                  className={classes.crewEfficiencyItem}
                  style={{ cursor: onCrewClick ? 'pointer' : 'default' }}
                  onClick={() => onCrewClick && onCrewClick(crew)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar 
                        style={{ 
                          backgroundColor: metrics.crewColor, 
                          width: 32, 
                          height: 32,
                          fontSize: '0.875rem'
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <Typography variant="subtitle2">
                        {crew.crewName}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTrendIcon(metrics.efficiency.trend)}
                      <Chip 
                        label={crew.score}
                        size="small"
                        color={getScoreColor(crew.score)}
                        className={classes.scoreChip}
                      />
                    </Box>
                  </Box>
                  
                  <div className={classes.efficiencyBar}>
                    <div 
                      className={classes.serviceBar}
                      style={{ width: `${crew.value}%` }}
                    >
                      <Build fontSize="small" style={{ marginRight: 4 }} />
                      {crew.value}%
                    </div>
                    <div 
                      className={classes.driveBar}
                      style={{ width: `${100 - crew.value}%` }}
                    >
                      <DirectionsCar fontSize="small" style={{ marginRight: 4 }} />
                      {100 - crew.value}%
                    </div>
                  </div>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="textSecondary">
                      {metrics.workload.weeklyHours.toFixed(1)}h worked
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(metrics.geographic.totalMiles)} miles
                    </Typography>
                  </Box>
                </div>
              );
            })}
          </div>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
            Needs Attention
          </Typography>
          <div style={{ height: 'calc(100% - 30px)', overflow: 'auto' }}>
            {/* Inefficient Crews */}
            {insights.inefficientCrews.map((insight) => (
              <Paper 
                key={`eff-${insight.crewId}`}
                style={{ 
                  marginBottom: 8,
                  padding: '12px 16px',
                  backgroundColor: '#fff3cd',
                  borderLeft: '4px solid #ff9800',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}
              >
                <Speed style={{ color: '#ff9800', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    {insight.crewName}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#856404' }}>
                    {insight.recommendation}
                  </Typography>
                </div>
              </Paper>
            ))}
            
            {/* Overworked Crews */}
            {insights.overworkedCrews.map((insight) => (
              <Paper 
                key={`ovr-${insight.crewId}`}
                style={{ 
                  marginBottom: 8,
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  borderLeft: '4px solid #f44336',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}
              >
                <Warning style={{ color: '#f44336', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    {insight.crewName}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#721c24' }}>
                    {insight.recommendation}
                  </Typography>
                </div>
              </Paper>
            ))}
            
            {/* Underutilized Crews */}
            {insights.underutilizedCrews.map((insight) => (
              <Paper 
                key={`und-${insight.crewId}`}
                style={{ 
                  marginBottom: 8,
                  padding: '12px 16px',
                  backgroundColor: '#d1ecf1',
                  borderLeft: '4px solid #2196f3',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8
                }}
              >
                <CheckCircle style={{ color: '#2196f3', marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    {insight.crewName}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#0c5460' }}>
                    {insight.recommendation}
                  </Typography>
                </div>
              </Paper>
            ))}
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EfficiencyOverviewPanel;